<template>
  <div class="flex items-center">
    <div class="relative w-full">
      <div
        v-if="average !== -1"
        class="top-1/2 left-[30%] translate-x-[-50%] translate-y-[-50%] absolute flex flex-col items-center w-fit"
        :class="{ 'legend-position': windowWidth < 500 }"
      >
        <span class="text-[#6469D1] font-bold text-4xl">{{ average }}%</span>
        <span class="text-[#9A9AAB] text-2xl">Average </span>
      </div>
      <v-chart ref="chartRef" :class="height" autoresize class="w-full" :option="pieChart" />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, ref, watch } from 'vue';
import { getChartTooltipPosition } from '@/helpers/charts';
import {
  EChartsOption,
  PieSeriesOption,
  TooltipComponentFormatterCallback,
  TooltipComponentPositionCallbackParams,
} from 'echarts';
import { useBreakpoints } from '@/composables/useBreakpoints';

export default defineComponent({
  props: {
    pieData: {
      type: Array as PropType<PieSeriesOption[]>,
      default: () => [],
    },
    formatter: {
      type: Function as PropType<
        TooltipComponentFormatterCallback<TooltipComponentPositionCallbackParams>
      >,
      default: () => () => `{c}`,
    },
    average: {
      type: Number,
      default: -1,
    },
    currentTab: { type: String, default: '' },
    height: { type: String, default: '' },
  },
  setup(props) {
    const chartRef = ref();

    const rightPositionChart = ref('0%');
    const radiusChart = ref(['100px', '150px']);
    const centerChart = ref(['30%', '50%']);
    const rerenderChart = ref(false);
    const labelPosition = ref('middle');
    const orientChart = ref<'horizontal' | 'vertical'>('vertical');

    const pieChart = computed<EChartsOption>(() => ({
      tooltip: {
        triggerOn: 'mousemove',
        trigger: 'item',
        className: 'pie-tooltip',
        position: getChartTooltipPosition,
        formatter: props.formatter,
      },
      grid: {
        left: '0%',
      },
      legend: {
        orient: orientChart.value,
        right: rightPositionChart.value,
        top: labelPosition.value,
        symbol: 'circle',
        itemWidth: 16,
        itemHeight: 16,
        icon: 'circle',
        textStyle: {
          color: '#545454',
          fontSize: 18,
          padding: [0, 0, 0, 10],
        },
        itemStyle: {
          borderWidth: 0,
        },
        borderRadius: 50,
      },
      emphasis: {
        disabled: true,
        scale: false,
      },

      series: [
        {
          name: 'Access From',
          type: 'pie',
          radius: radiusChart.value,
          center: centerChart.value,
          avoidLabelOverlap: false,
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '40',
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: props.pieData,
        },
      ],
    }));
    const { windowWidth } = useBreakpoints();

    onMounted(() => {
      mobileVariantChart();
    });

    watch(windowWidth, () => {
      mobileVariantChart();
    });

    const downloadChart = () => {
      return chartRef.value.getDataURL();
    };

    const mobileVariantChart = () => {
      if (windowWidth.value > 2100) {
        rightPositionChart.value = '30%';
      } else rightPositionChart.value = '0%';

      if (windowWidth.value < 500) {
        radiusChart.value = ['90px', '140px'];
        centerChart.value = ['50%', '50%'];
        rerenderChart.value = true;
        labelPosition.value = 'bottom';
        orientChart.value = 'horizontal';
      } else {
        radiusChart.value = ['100px', '140px'];
        centerChart.value = ['30%', '50%'];
        labelPosition.value = 'middle';
        rerenderChart.value = false;
        orientChart.value = 'vertical';
      }
      if (props.currentTab === 'HOLD_BY_CUSTOMER' && windowWidth.value > 1024) {
        rightPositionChart.value = '40%';
      } else if (props.currentTab === 'HOLD_BY_CUSTOMER') rightPositionChart.value = '10%';
    };

    // const getPieItem = (name: string) => {
    //   const currentItem = props.pieData.find((el) => el.name.includes(name));
    //   if (currentItem) {
    //     const checkIfBigger = (currentItem.value > 50).toString();
    //     return `${checkIfBigger[0].toUpperCase() + checkIfBigger.slice(1)} ${currentItem.value}%`;
    //   }
    //   return name;
    // };

    return {
      chartRef,
      pieChart,
      windowWidth,
      downloadChart,
    };
  },
});
</script>

<style lang="scss">
.pie-tooltip {
  border: 1px solid rgba(124, 124, 150, 0.19) !important;
  border-radius: 6px !important;
  position: relative;
  z-index: 1000 !important;
  display: none;

  &:before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    border-left: 1px solid rgba(124, 124, 150, 0.19) !important;
    border-bottom: 1px solid rgba(124, 124, 150, 0.19) !important;
    background: white;
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translate(-50%, 0%) rotate(-45deg);
    z-index: -1 !important;
  }
}

.legend-position {
  top: 50% !important;
  right: 34% !important;
  transform: translate(34%, -50%) !important;
}
</style>
